import { useEffect, useRef } from 'react'
import clsx from 'clsx'
import { Document, Folder } from '@/assets/icons'
import { SearchableText } from '@/components/searchable-text/SearchableText'
import type { SearchSuggestion } from '../hooks/useSearchSuggestions'

type SearchSuggestionRowProps = {
  suggestion: SearchSuggestion
  /** The live query, so the matched substring can be bolded. */
  query: string
  /** Element id, referenced by the input's `aria-activedescendant`. */
  id: string
  active: boolean
  onSelect: () => void
  onHover: () => void
}

/**
 * One suggestion: icon, name with the matched substring bolded, and the item's full location.
 *
 * `active` is the only highlight — there is deliberately no CSS `:hover` state. Pointing at a row
 * makes it active, so the row that looks chosen is always the row Enter will take, even when the
 * list opens under a cursor that never moves.
 */
export function SearchSuggestionRow({ suggestion, query, id, active, onSelect, onHover }: SearchSuggestionRowProps) {
  const Icon = suggestion.kind === 'folder' ? Folder : Document
  const rowRef = useRef<HTMLDivElement | null>(null)

  // The list scrolls once it is full, so walking it with the arrow keys has to bring the active row
  // along — otherwise `aria-activedescendant` points at something off screen.
  useEffect(() => {
    if (active) rowRef.current?.scrollIntoView({ block: 'nearest' })
  }, [active])

  return (
    <div
      ref={rowRef}
      id={id}
      role="option"
      aria-selected={active}
      // Spelled out, because bolding the matched substring splits the name the accessible-name
      // calculation would otherwise build — "Cast illo_Depo.pdf" is what a screen reader would say.
      aria-label={`${suggestion.name}, ${suggestion.locationLabel}`}
      className={clsx('flex h-10 w-full cursor-pointer items-center justify-between gap-2 rounded p-2 text-left', active && 'bg-brand-neutral-100')}
      // Mousedown would blur the input and close the dropdown before the click lands.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onSelect}
      onMouseEnter={onHover}
    >
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <Icon className={clsx('h-4 w-4 shrink-0', active ? 'text-brand-neutral-800' : 'text-brand-neutral-600')} aria-hidden />
        <span className={clsx('truncate text-sm leading-5 tracking-[-0.15px]', active ? 'text-brand-neutral-800' : 'text-brand-neutral-700')}>
          <SearchableText text={suggestion.name} searchQuery={query} />
        </span>
      </span>

      {/* Capped rather than fixed: a deep path ellipsizes instead of squeezing the name beside it. */}
      <span className="hidden min-w-0 max-w-[45%] truncate pl-4 text-sm leading-5 text-brand-neutral-600 md:block">{suggestion.locationLabel}</span>
    </div>
  )
}
